/**
 * Search on the television.
 *
 * The same endpoint and the same debounce as the web screen; what differs is that every
 * result must be reachable with four arrow keys and a select. That rules out reusing the
 * web component with different styling — a grid of plain links is unreachable here, and
 * the focus graph has to know about each card.
 *
 * ## The keyboard problem, and why voice is first
 *
 * Typing an email address with a remote is unpleasant. Typing a film title is worse,
 * because you do it every time you want to watch something. So the voice button is the
 * first focusable on the screen when the panel can listen — one press, say the name — and
 * the on-screen keyboard is the fallback rather than the default path.
 *
 * Where the platform offers no recogniser the button is not drawn at all, and focus starts
 * in the text field. The television never *depends* on anything else being present.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CatalogItemDto } from '@filmodasi/shared';
import { api } from '../../../api/client';
import { Empty, ErrorBox, Spinner } from '../../../components/ui';
import { Icon } from '../../../components/Icon';
import { VoiceIndicator, type VoiceStep } from '../../../components/VoiceIndicator';
import { TvCard, TvRail } from '../TvShell';
import { useFocusable } from '../focus/useFocusable';
import { detailPath } from '../../../catalog/detailPath';
import { useVoiceSearch } from '../../../voice/useVoiceSearch';

interface SearchResults {
  live: CatalogItemDto[];
  movies: CatalogItemDto[];
  series: CatalogItemDto[];
}

const DEBOUNCE_MS = 320;
const MIN_QUERY = 2;

/** The text field, registered so the remote can reach it and open the panel's keyboard. */
function SearchField({
  value,
  onChange,
  autoFocus,
}: {
  value: string;
  onChange: (next: string) => void;
  autoFocus: boolean;
}) {
  const { ref, elementRef, focused } = useFocusable<HTMLInputElement>({
    id: 'search:field',
    group: 'search:controls',
    autoFocus,
    // Selecting the field opens the platform keyboard, which is what `focus()` does on
    // Tizen; there is nothing else for Enter to mean here.
    onSelect: () => elementRef.current?.focus(),
  });

  return (
    <input
      ref={ref}
      type="search"
      className={`tvsearch__field${focused ? ' is-focused' : ''}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Ara"
      aria-label="Ara"
    />
  );
}

function VoiceButton({
  listening,
  onToggle,
}: {
  listening: boolean;
  onToggle: () => void;
}) {
  const { ref, focused } = useFocusable<HTMLButtonElement>({
    id: 'search:voice',
    group: 'search:controls',
    // First focus on the screen: it is the way in that does not involve the keyboard.
    autoFocus: true,
    onSelect: onToggle,
  });
  return (
    <button
      ref={ref}
      type="button"
      className={`tvsearch__voice${focused ? ' is-focused' : ''}${listening ? ' is-listening' : ''}`}
      onClick={onToggle}
    >
      {/* Was `🎤` and `●`. An emoji is whatever the panel's font decides — on Tizen that
          is a flat monochrome glyph at an unpredictable size, sitting on the one control
          the whole screen is designed around. */}
      <Icon name="mic" size={1.1} />
      {listening ? 'Dinliyor…' : 'Sesle ara'}
    </button>
  );
}

export function TvSearchPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const voice = useVoiceSearch({ onResult: (text) => setQuery(text) });

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY) {
      setResults(null);
      return;
    }

    // Same two guards as the web screen: the timer collapses keystrokes, the flag stops a
    // slow earlier request from overwriting a newer one.
    let cancelled = false;
    setLoading(true);

    const timer = window.setTimeout(() => {
      api<SearchResults>(`/api/catalog?q=${encodeURIComponent(trimmed)}&limit=20`)
        .then((data) => {
          if (!cancelled) {
            setResults(data);
            setError(null);
          }
        })
        .catch((err: unknown) => {
          if (!cancelled) setError(err instanceof Error ? err.message : 'Arama başarısız');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);

  const total = results
    ? results.live.length + results.movies.length + results.series.length
    : 0;

  const groups: { key: string; title: string; items: CatalogItemDto[] }[] = results
    ? [
        { key: 'movies', title: 'Filmler', items: results.movies },
        { key: 'series', title: 'Diziler', items: results.series },
        { key: 'live', title: 'Canlı TV', items: results.live },
      ].filter((g) => g.items.length > 0)
    : [];

  /* No `heard` step on the television: `webapis.voicecontrol` delivers one final command
     and nothing before it, so there is no interim to confirm and the query runs at once. */
  const voiceStep: VoiceStep = voice.error ? 'error' : voice.listening ? 'listening' : 'idle';

  return (
    <>
      <div className="tvsearch__bar">
        {voice.supported && <VoiceButton listening={voice.listening} onToggle={voice.listening ? voice.stop : voice.start} />}
        <SearchField value={query} onChange={setQuery} autoFocus={!voice.supported} />
      </div>

      {/*
        The same indicator the web panel uses.

        It was two conditional paragraphs — an error and an interim transcript — with no
        state at all between "not listening" and "heard the whole thing", which on the panel
        is the gap where somebody presses the button, says a title, and watches a screen
        that has not changed. The ring is CSS on a non-focusable span, so it is inside the
        television's motion rule rather than an exception to it.
      */}
      <VoiceIndicator step={voiceStep} interim={voice.interim} message={voice.error} />
      {error && <ErrorBox message={error} />}

      {loading && !results && <Spinner />}
      {results && total === 0 && !loading && <Empty>“{query.trim()}” için sonuç yok.</Empty>}

      {groups.map((group) => (
        <TvRail title={group.title} key={group.key}>
          {group.items.map((item, col) => (
            <TvCard
              key={item.id}
              id={`search:${group.key}:${col}`}
              group={`search:${group.key}`}
              title={item.title}
              subtitle={item.year ? String(item.year) : null}
              art={item.poster ?? item.logo}
              poster={group.key !== 'live'}
              onSelect={() => navigate(detailPath(item))}
            />
          ))}
        </TvRail>
      ))}

      {!results && !loading && (
        <p className="tvsearch__hint">
          {voice.supported
            ? 'Sesle arayın ya da yazın. Oyuncu ve tür adları da aranır.'
            : 'Bir isim yazın. Oyuncu ve tür adları da aranır.'}
        </p>
      )}
    </>
  );
}
